<template>
  <v-container fluid>
    <v-form ref="form" @submit.prevent="save(false)">
      <v-row>
        <form-header :title="isEditing ?'Modifica pacchetto' : 'Nuovo pacchetto'"
                     @back="$router.replace('/kits')" @saveback="save(true)" :loading="loading"></form-header>
        <v-col cols="12" >
          <h3>Info generali</h3>
        </v-col>
        <v-col cols="12" md="6" lg="4" xl="3">
          <v-text-field
              label="Ordinamento*"
              v-model="editingItem.order"
              :rules="[rules.required, rules.minInt(0)]"
              type="number"
              step="1"
              min="0"
          ></v-text-field>
        </v-col>
        <v-col cols="12" md="6" lg="4" xl="3">
          <v-text-field
              label="Titolo*"
              v-model="editingItem.title.it"
              :rules="[rules.required]"
          ></v-text-field>
        </v-col>
        <v-col cols="12" md="6" lg="4" xl="3">
          <v-select
              label="Categoria*"
              v-model="editingItem.category"
              :rules="[rules.required]"
              :items="categoryOptions"
          ></v-select>
        </v-col>

        <v-col cols="12" md="6" lg="4" xl="3">
          <v-select
              label="Genere*"
              v-model="editingItem.genre"
              :rules="[rules.required]"
              :items="genreOptions"
          ></v-select>
        </v-col>

        <v-col cols="12" md="6" lg="4" xl="3">
          <v-select
              label="Difficoltà*"
              v-model="editingItem.difficulty"
              :rules="[rules.required]"
              :items="difficultyOptions"
          ></v-select>
        </v-col>
        <v-col cols="12" md="6" lg="4" xl="3">
          <v-switch
              label="Pubblicato"
              v-model="editingItem.pub"
              color="primary"
          ></v-switch>
        </v-col>
        <v-col cols="12" >
          <h3>Media</h3>
        </v-col>

        <v-col cols="12" lg="8" xl="6">
          <media-field label="Immagine copertina" v-model="editingItem.cover" media-type="cover" required></media-field>
        </v-col>

        <v-col cols="12" lg="3" xl="2">
          <media-field label="Immagine icona" v-model="editingItem.icon" media-type="icon" required :aspect-ratio="1"></media-field>
        </v-col>

        <v-col cols="12" >
          <h3>Descrizione</h3>
        </v-col>
        <v-col cols="12" lg="8" xl="6">
          <v-textarea label="Descrizione*" v-model="editingItem.description.it" :rules="[rules.required]"></v-textarea>
        </v-col>


        <form-footer @back="$router.replace('/kits')" @saveback="save(true)" :loading="loading"></form-footer>
      </v-row>
    </v-form>
  </v-container>
</template>

<script lang="ts">
import utils from "../utils";
import rules from "../utils/rules";
import FormHeader from "@/components/FormHeader.vue";
import FormFooter from "@/components/FormFooter.vue";
import {computed, inject, onMounted, ref, useTemplateRef} from "vue";
import type {Auth} from "@/plugins/auth.ts";
import {type Messages, Msg} from "@/plugins/messages.ts";
import type {Globals} from "@/plugins/globals.ts";
import {useRoute, useRouter} from "vue-router";
import api from "@/api";
import {VForm} from "vuetify/components";
import type { Kit } from "@/models.ts";
import MediaField from "@/components/MediaField.vue";

export default {
  components: {MediaField, FormFooter, FormHeader},
  setup() {
    const auth = inject('auth') as Auth
    const messages = inject('messages') as Messages
    const globals = inject('globals') as Globals
    const route = useRoute()
    const router = useRouter()
    const form = useTemplateRef<typeof VForm>('form')
    const loading = ref(false)

    const editingItem = ref<Kit>({
      category: "",
      cover: "",
      description: { it : '' },
      difficulty: "",
      genre: "",
      icon: "",
      order: 0,
      pub: false,
      title: { it : ''}
    })

    const loadData = async () => {
      loading.value = true;
      let id = route.params.id as string;
      const res = await api.get<Kit>('kits', id)
      if(!res.success) {
        loading.value = false;
        messages.error(res.message ?? Msg.serverError)
        await router.replace('/kits')
        return
      }
      editingItem.value = res.data!
      loading.value = false;
    }

    onMounted(async ()=>{
      await globals.waitForInit
      await router.isReady()
      await globals.refresh('media')
      if (route.params.id !== "new") {
        await loadData();
      }
    })




    return {
      form,
      loading,
      isEditing : computed(()=> route.params.id !== "new"),
      editingItem,
      rules,
      formatDate : utils.formatDate,
      categoryOptions : computed(()=>{
        return globals.categories.value.map(c => {
          return { title : c.name.it, value : c._id }
        })
      }),
      genreOptions : computed(()=>{
        return globals.genre.value.map(c => {
          return { title : c.name.it, value : c._id }
        })
      }),
      difficultyOptions : computed(()=>{
        return globals.difficulties.value.map(c => {
          return { title : c.name.it, value : c._id }
        })
      }),
      async save(goBackAfterSave:boolean) {
        const v = await form.value!.validate()
        if(!v.valid) return;
        loading.value = true;

        editingItem.value.order = utils.toInt(editingItem.value.order, 0)
        const res = await api.save<Kit>('kits', editingItem.value)
        loading.value = false;
        if(!res.success) {
          messages.error(res.message ?? Msg.serverError)
          if(res.status === 401) {
            auth.logout()
          }
          return
        }
        editingItem.value = res.data!
        messages.success("Pacchetto salvato")
        if(goBackAfterSave) {
          await router.replace('/kits')
        } else if(route.params.id === 'new'){
          await router.replace(`/kits/${editingItem.value._id}`)
        }
      }
    };
  }
};
</script>

<style scoped></style>
